import { useEffect, useState, useMemo, useRef } from "react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import api, { apiError, fileUrl } from "@/lib/api";
import { TRY, NUM, fmtDate, todayISO } from "@/lib/format";
import { PageHeader, StatCard, Panel, Segmented, Empty } from "@/components/Shared";
import { cn } from "@/lib/utils";
import { DatePicker } from "@/components/DatePicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  PieChart, Pie, Cell as PieCell, AreaChart, Area,
} from "recharts";
import { Printer, FileDown, Package, ArrowDownCircle, ArrowUpCircle, Wallet, Percent, FileText, Loader2, Search, BarChart3, PieChart as PieIcon, TrendingUp, Layers, Crown, Truck, Flame, X } from "lucide-react";
import { toast } from "sonner";

const MONTHS = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const PIE = ["#EA5820", "#2563EB", "#10B981", "#F59E0B", "#8B5CF6", "#EF4444", "#0EA5E9"];
const LEGEND = { paddingTop: "12px", fontSize: "12px", fontFamily: "Chivo, sans-serif" };
const GRID = "hsl(214 15% 88%)";
const costOf = (p) => Number(p.cost ?? p.price ?? 0);
const ROW_CAP = 200;

// Koyu slate tooltip — para birimi tr-TR
const ChartTip = ({ active, payload, label, money = true }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg bg-slate-900 text-white px-3 py-2 shadow-xl text-xs min-w-[140px]" data-testid="chart-tooltip">
      {label && <div className="font-head font-bold text-[11px] uppercase tracking-wider text-slate-300 mb-1">{label}</div>}
      {payload.map((p, i) => (
        <div key={i} className="flex items-center justify-between gap-4 py-0.5">
          <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm" style={{ background: p.color || p.payload?.fill }} />{p.name}</span>
          <b className="tabular-nums font-mono">{money ? TRY(p.value) : NUM(p.value)}</b>
        </div>
      ))}
    </div>
  );
};

export default function Reports() {
  const [products, setProducts] = useState([]);
  const [movements, setMovements] = useState([]);
  const [summary, setSummary] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [tab, setTab] = useState("stok");
  const [q, setQ] = useState("");
  const now = new Date();
  const [repDlg, setRepDlg] = useState(false);
  const [repMonth, setRepMonth] = useState(now.getMonth());
  const [repYear, setRepYear] = useState(now.getFullYear());
  const [repData, setRepData] = useState(null);
  const [repGen, setRepGen] = useState(false);
  const printRef = useRef(null);
  const [topSelling, setTopSelling] = useState([]);
  const [tsMetric, setTsMetric] = useState("qty");
  const [tsBrand, setTsBrand] = useState("");
  const [tsStart, setTsStart] = useState("");
  const [tsEnd, setTsEnd] = useState("");
  const [tsLoading, setTsLoading] = useState(false);
  const [brandProf, setBrandProf] = useState([]);
  const [bpTot, setBpTot] = useState(null);
  const [bpMetric, setBpMetric] = useState("profit");
  const [bpStart, setBpStart] = useState("");
  const [bpEnd, setBpEnd] = useState("");
  const [bpLoading, setBpLoading] = useState(false);

  const loadTopSelling = async () => {
    setTsLoading(true);
    try {
      const p = new URLSearchParams({ metric: tsMetric, limit: "20" });
      if (tsBrand) p.set("brand", tsBrand);
      if (tsStart) p.set("start", tsStart);
      if (tsEnd) p.set("end", tsEnd);
      const { data } = await api.get(`/reports/top-products?${p.toString()}`);
      setTopSelling(data.items || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setTsLoading(false); }
  };
  useEffect(() => { loadTopSelling(); /* eslint-disable-next-line */ }, [tsMetric, tsBrand, tsStart, tsEnd]);
  const tsExport = () => exportSheet(topSelling.map((r, i) => ({ "#": i + 1, "Ürün": r.name, "Kod": r.code, "Adet": r.qty, "Hasılat": r.revenue, "Kâr": r.profit })), "en-cok-satilan");

  const loadBrandProfit = async () => {
    setBpLoading(true);
    try {
      const p = new URLSearchParams({ metric: bpMetric, limit: "100" });
      if (bpStart) p.set("start", bpStart);
      if (bpEnd) p.set("end", bpEnd);
      const { data } = await api.get(`/reports/brand-profit?${p.toString()}`);
      setBrandProf(data.items || []); setBpTot(data.totals || null);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBpLoading(false); }
  };
  useEffect(() => { loadBrandProfit(); /* eslint-disable-next-line */ }, [bpMetric, bpStart, bpEnd]);
  const bpExport = () => exportSheet(brandProf.map((r, i) => ({ "#": i + 1, "Marka": r.brand, "Adet": r.qty, "Hasılat": r.revenue, "Maliyet": r.cost, "Kâr": r.profit, "Marj %": r.margin })), "marka-karlilik");

  const fetchMonthly = async () => {
    setRepGen(true);
    try {
      const { data } = await api.get(`/reports/monthly?year=${repYear}&month=${repMonth + 1}`);
      setRepData(data);
    } catch (e) { toast.error(apiError(e)); setRepGen(false); }
  };

  useEffect(() => {
    if (!repData) return;
    const t = setTimeout(async () => {
      try {
        const canvas = await html2canvas(printRef.current, { scale: 2, backgroundColor: "#ffffff", useCORS: true });
        const pdf = new jsPDF("p", "mm", "a4");
        const pw = pdf.internal.pageSize.getWidth();
        const ph = pdf.internal.pageSize.getHeight();
        const imgH = (canvas.height * pw) / canvas.width;
        const img = canvas.toDataURL("image/png");
        let heightLeft = imgH, pos = 0;
        pdf.addImage(img, "PNG", 0, pos, pw, imgH);
        heightLeft -= ph;
        while (heightLeft > 0) { pos -= ph; pdf.addPage(); pdf.addImage(img, "PNG", 0, pos, pw, imgH); heightLeft -= ph; }
        pdf.save(`aylik-rapor-${repData.period}.pdf`);
        toast.success("Aylık rapor PDF indirildi");
        setRepDlg(false);
      } catch { toast.error("PDF oluşturulamadı"); }
      finally { setRepData(null); setRepGen(false); }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [repData]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/stock/movements").then((r) => setMovements(r.data)).catch(() => {});
    api.get("/dashboard/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/contacts").then((r) => setContacts(r.data)).catch(() => {});
  }, []);

  const incoming = movements.filter((m) => m.type === "giris");
  const outgoing = movements.filter((m) => m.type === "cikis");
  const stockCost = products.reduce((s, p) => s + p.quantity * costOf(p), 0);
  const stockSale = products.reduce((s, p) => s + p.quantity * Number(p.price || 0), 0);

  const categoryData = useMemo(() => {
    const m = {};
    products.forEach((p) => { const k = p.category || "Diğer"; m[k] = (m[k] || 0) + p.quantity * costOf(p); });
    return Object.entries(m).map(([name, value]) => ({ name, value: Math.round(value) })).filter((x) => x.value > 0).sort((a, b) => b.value - a.value).slice(0, 7);
  }, [products]);

  const topProducts = useMemo(() =>
    [...products].map((p) => ({ name: p.name.length > 18 ? p.name.slice(0, 18) + "…" : p.name, deger: Math.round(p.quantity * costOf(p)) }))
      .filter((x) => x.deger > 0).sort((a, b) => b.deger - a.deger).slice(0, 8), [products]);

  const moveTrend = useMemo(() => {
    const d0 = new Date();
    const arr = [];
    for (let k = 5; k >= 0; k--) {
      const d = new Date(d0.getFullYear(), d0.getMonth() - k, 1);
      const pre = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      arr.push({
        month: `${MONTHS[d.getMonth()].slice(0, 3)} ${String(d.getFullYear()).slice(2)}`,
        giris: movements.filter((m) => m.type === "giris" && String(m.created_at).startsWith(pre)).length,
        cikis: movements.filter((m) => m.type === "cikis" && String(m.created_at).startsWith(pre)).length,
      });
    }
    return arr;
  }, [movements]);

  const topBuyers = useMemo(() => [...contacts].filter((c) => (c.total_sold || 0) > 0).sort((a, b) => (b.total_sold || 0) - (a.total_sold || 0)).slice(0, 8), [contacts]);
  const topSellers = useMemo(() => [...contacts].filter((c) => (c.total_bought || 0) > 0).sort((a, b) => (b.total_bought || 0) - (a.total_bought || 0)).slice(0, 8), [contacts]);
  const brands = useMemo(() => [...new Set(products.map((p) => p.brand).filter(Boolean))].sort((a, b) => a.localeCompare(b, "tr")), [products]);

  const exportSheet = (rows, name) => {
    if (!rows.length) return toast.error("Dışa aktarılacak kayıt yok");
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, name);
    XLSX.writeFile(wb, `gumusalan-${name}_${todayISO()}.xlsx`);
    toast.success(`${rows.length} satır Excel'e aktarıldı`);
  };

  const s = q.trim().toLocaleLowerCase("tr");
  const hit = (...vals) => !s || vals.some((v) => String(v || "").toLocaleLowerCase("tr").includes(s));
  const stockRows = useMemo(() => [...products].filter((p) => hit(p.name, p.brand, p.code, p.category)).sort((a, b) => b.quantity * costOf(b) - a.quantity * costOf(a)), [products, s]); // eslint-disable-line react-hooks/exhaustive-deps
  const moveRows = useMemo(() => (tab === "gelen" ? incoming : outgoing).filter((m) => hit(m.product_name, m.user_name, m.note)), [tab, movements, s]); // eslint-disable-line react-hooks/exhaustive-deps

  const stockExport = () => exportSheet(stockRows.map((p) => ({ Ürün: p.name, Marka: p.brand, Kod: p.code, Kategori: p.category, Stok: p.quantity, Birim: p.unit, "Birim Maliyet": costOf(p), "Satış Fiyatı": p.price, "Stok Değeri (Maliyet)": costOf(p) * p.quantity })), "stok-raporu");
  const moveExport = () => exportSheet(moveRows.map((m) => ({ Tarih: fmtDate(m.created_at), Ürün: m.product_name, Miktar: m.quantity, Personel: m.user_name, Not: m.note })), tab === "gelen" ? "gelen-mal" : "giden-mal");

  return (
    <div className="space-y-6 stagger-in" data-testid="reports-page">
      <PageHeader title="Raporlar" badge="Analitik" subtitle="Stok değeri, alış/satış trendi, mal hareketleri ve en çok işlem yapılan cariler — Excel ve PDF dışa aktarımla">
        <Button onClick={() => setRepDlg(true)} data-testid="monthly-report-btn"><FileText className="h-4 w-4 mr-2" />Aylık Rapor PDF</Button>
        <Button variant="outline" onClick={() => window.print()} data-testid="print-btn"><Printer className="h-4 w-4 mr-2" />Yazdır</Button>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 no-print" data-testid="report-kpis">
        <StatCard label="Toplam Ürün" value={NUM(products.length)} icon={Package} sub={`${products.filter((p) => p.quantity > 0).length} stokta`} testid="report-kpi-products" onClick={() => setTab("stok")} active={tab === "stok"} />
        <StatCard label="Stok Değeri (Maliyet)" value={TRY(stockCost)} icon={Wallet} tone="primary" sub={`Satış değeri ${TRY(stockSale)}`} testid="report-kpi-stock-value" />
        <StatCard label="Giriş Hareketi" value={NUM(incoming.length)} icon={ArrowDownCircle} tone="blue" sub="stok girişi" testid="report-kpi-in" onClick={() => setTab("gelen")} active={tab === "gelen"} />
        <StatCard label="Çıkış Hareketi" value={NUM(outgoing.length)} icon={ArrowUpCircle} tone="danger" sub="stok çıkışı" testid="report-kpi-out" onClick={() => setTab("giden")} active={tab === "giden"} />
        <StatCard label="Ödenecek KDV" value={summary ? TRY(summary.kdv_balance) : "—"} icon={Percent} tone="success" sub="hesaplanan − indirilecek" testid="report-kpi-kdv" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 no-print">
        <Panel title="Alış / Satış" subtitle="Son 6 ay · fatura toplamları" icon={BarChart3} testid="chart-buy-sell">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={summary?.chart || []} barGap={4}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={GRID} />
              <XAxis dataKey="month" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
              <Tooltip content={<ChartTip />} cursor={{ fill: "hsl(210 20% 96%)" }} />
              <Legend wrapperStyle={LEGEND} />
              <Bar dataKey="alis" name="Alış" fill="#2563EB" radius={[4, 4, 0, 0]} />
              <Bar dataKey="satis" name="Satış" fill="#EA5820" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Kategori Dağılımı" subtitle="Stok değeri (maliyet) · ilk 7 kategori" icon={PieIcon} testid="chart-category">
          {categoryData.length === 0 ? <Empty title="Veri yok" /> : (
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={categoryData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={95} innerRadius={55} paddingAngle={2} stroke="none">
                    {categoryData.map((_, i) => <PieCell key={i} fill={PIE[i % PIE.length]} />)}
                  </Pie>
                  <Tooltip content={<ChartTip />} />
                </PieChart>
              </ResponsiveContainer>
              <ul className="w-full sm:w-64 space-y-1.5 text-xs" data-testid="chart-category-legend">
                {categoryData.map((c, i) => {
                  const pct = stockCost ? Math.round((c.value / stockCost) * 100) : 0;
                  return (
                    <li key={c.name} className="flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-sm shrink-0" style={{ background: PIE[i % PIE.length] }} />
                      <span className="truncate flex-1" title={c.name}>{c.name}</span>
                      <span className="font-mono tabular-nums text-muted-foreground">{pct}%</span>
                      <b className="font-mono tabular-nums">{TRY(c.value)}</b>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </Panel>

        <Panel title="En Değerli 8 Ürün" subtitle="Stok değeri (maliyet × adet)" icon={Layers} testid="chart-top-products">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={topProducts} layout="vertical" margin={{ left: 12, right: 24 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={GRID} />
              <XAxis type="number" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
              <YAxis type="category" dataKey="name" fontSize={11} width={130} tickLine={false} axisLine={false} />
              <Tooltip content={<ChartTip />} cursor={{ fill: "hsl(210 20% 96%)" }} />
              <Bar dataKey="deger" name="Stok Değeri" fill="#EA5820" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Mal Hareketi" subtitle="Son 6 ay · giriş / çıkış işlem sayısı" icon={TrendingUp} testid="chart-movements">
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={moveTrend}>
              <defs>
                <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10B981" stopOpacity={0.45} /><stop offset="95%" stopColor="#10B981" stopOpacity={0} /></linearGradient>
                <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#EF4444" stopOpacity={0.45} /><stop offset="95%" stopColor="#EF4444" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={GRID} />
              <XAxis dataKey="month" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip content={<ChartTip money={false} />} />
              <Legend wrapperStyle={LEGEND} />
              <Area type="monotone" dataKey="giris" name="Giriş" stroke="#10B981" strokeWidth={2} fill="url(#g1)" />
              <Area type="monotone" dataKey="cikis" name="Çıkış" stroke="#EF4444" strokeWidth={2} fill="url(#g2)" />
            </AreaChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 no-print" data-testid="reports-top-contacts">
        <TopContactCard title="En Çok Alan Müşteriler" subtitle="Bize en çok satış yaptığımız cariler" testid="report-top-buyers" rows={topBuyers} field="total_sold" color="#16A34A" icon={Crown} />
        <TopContactCard title="En Çok Alışveriş Yapılan Tedarikçiler" subtitle="En çok mal aldığımız toptancılar" testid="report-top-sellers" rows={topSellers} field="total_bought" color="#2563EB" icon={Truck} />
      </div>

      <Panel title="En Çok Satılan Ürünler" subtitle="Hızlı satış + satış faturaları · gerçek satış verisine göre" icon={Flame} className="no-print" testid="report-top-selling"
        actions={<div className="hidden sm:block"><Segmented value={tsMetric} onChange={setTsMetric} testid="ts-metric" options={[["qty", "Adet"], ["revenue", "Hasılat"], ["profit", "Kâr"]]} /></div>}>
        <div className="flex flex-col sm:flex-row gap-2 mb-4 flex-wrap items-start">
          <div className="sm:hidden w-full"><Segmented value={tsMetric} onChange={setTsMetric} testid="ts-metric-m" options={[["qty", "Adet"], ["revenue", "Hasılat"], ["profit", "Kâr"]]} /></div>
          <Select value={tsBrand || "__all"} onValueChange={(v) => setTsBrand(v === "__all" ? "" : v)}>
            <SelectTrigger className="w-full sm:w-52 h-9" data-testid="ts-brand"><SelectValue placeholder="Marka" /></SelectTrigger>
            <SelectContent><SelectItem value="__all">Tüm Markalar</SelectItem>{brands.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
          </Select>
          <div className="w-40"><DatePicker value={tsStart} onChange={setTsStart} placeholder="Başlangıç" data-testid="ts-start" /></div>
          <div className="w-40"><DatePicker value={tsEnd} onChange={setTsEnd} placeholder="Bitiş" data-testid="ts-end" /></div>
          {(tsStart || tsEnd || tsBrand) && <Button size="sm" variant="ghost" className="h-9" onClick={() => { setTsStart(""); setTsEnd(""); setTsBrand(""); }} data-testid="ts-clear"><X className="h-4 w-4 mr-1" />Temizle</Button>}
          <Button size="sm" variant="outline" className="h-9 sm:ml-auto" onClick={tsExport} data-testid="ts-export"><FileDown className="h-4 w-4 mr-1.5 text-emerald-600" />Excel</Button>
        </div>
        {tsLoading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : topSelling.length === 0 ? (
          <Empty title="Satış verisi yok" desc="Seçilen filtrelerde satılan ürün bulunamadı." />
        ) : (
          <div className="border rounded-xl overflow-x-auto" data-testid="ts-table">
            <Table>
              <TableHeader><TableRow className="bg-muted/50">
                <TableHead className="w-10">#</TableHead><TableHead>Ürün</TableHead>
                <TableHead className="text-right">Adet</TableHead><TableHead className="text-right">Hasılat</TableHead><TableHead className="text-right">Kâr</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {topSelling.map((r, i) => (
                  <TableRow key={r.product_id} data-testid={`ts-row-${i}`}>
                    <TableCell><span className="h-6 w-6 rounded-md text-[11px] font-black flex items-center justify-center font-head bg-primary/10 text-primary">{i + 1}</span></TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2.5">
                        {r.image_url ? <img src={fileUrl(r.image_url)} alt="" loading="lazy" className="h-9 w-9 rounded object-contain bg-white border shrink-0" /> : <div className="h-9 w-9 rounded bg-muted flex items-center justify-center shrink-0"><Package className="h-4 w-4 text-muted-foreground/40" /></div>}
                        <div className="min-w-0"><div className="font-semibold text-sm truncate" title={r.name}>{r.name}</div>{r.code && <div className="text-[11px] font-mono text-muted-foreground">{r.code}</div>}</div>
                      </div>
                    </TableCell>
                    <TableCell className={cn("text-right tabular-nums font-mono", tsMetric === "qty" && "font-bold text-primary")}>{NUM(r.qty)}</TableCell>
                    <TableCell className={cn("text-right tabular-nums font-mono", tsMetric === "revenue" && "font-bold text-primary")}>{TRY(r.revenue)}</TableCell>
                    <TableCell className={cn("text-right tabular-nums font-mono", tsMetric === "profit" ? (r.profit >= 0 ? "font-bold text-emerald-600" : "font-bold text-destructive") : "text-muted-foreground")}>{TRY(r.profit)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Panel>

      <Panel title="Marka Kârlılık Raporu" subtitle="En kârlı markalar · hızlı satış + satış faturaları (kâr = satış − maliyet)" icon={TrendingUp} className="no-print" testid="report-brand-profit"
        actions={<div className="hidden sm:block"><Segmented value={bpMetric} onChange={setBpMetric} testid="bp-metric" options={[["profit", "Kâr"], ["revenue", "Hasılat"], ["qty", "Adet"]]} /></div>}>
        <div className="flex flex-col sm:flex-row gap-2 mb-4 flex-wrap items-start">
          <div className="sm:hidden w-full"><Segmented value={bpMetric} onChange={setBpMetric} testid="bp-metric-m" options={[["profit", "Kâr"], ["revenue", "Hasılat"], ["qty", "Adet"]]} /></div>
          <div className="w-40"><DatePicker value={bpStart} onChange={setBpStart} placeholder="Başlangıç" data-testid="bp-start" /></div>
          <div className="w-40"><DatePicker value={bpEnd} onChange={setBpEnd} placeholder="Bitiş" data-testid="bp-end" /></div>
          {(bpStart || bpEnd) && <Button size="sm" variant="ghost" className="h-9" onClick={() => { setBpStart(""); setBpEnd(""); }} data-testid="bp-clear"><X className="h-4 w-4 mr-1" />Temizle</Button>}
          <Button size="sm" variant="outline" className="h-9 sm:ml-auto" onClick={bpExport} data-testid="bp-export"><FileDown className="h-4 w-4 mr-1.5 text-emerald-600" />Excel</Button>
        </div>
        {bpTot && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4" data-testid="bp-totals">
            <RepBox label="Toplam Hasılat" value={TRY(bpTot.revenue)} color="#2563EB" />
            <RepBox label="Toplam Maliyet" value={TRY(bpTot.cost)} color="#EA5820" />
            <RepBox label="Toplam Kâr" value={TRY(bpTot.profit)} color={bpTot.profit >= 0 ? "#16A34A" : "#DC2626"} />
            <RepBox label="Ort. Kâr Marjı" value={`%${NUM(bpTot.margin)}`} color="#16A34A" />
          </div>
        )}
        {bpLoading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : brandProf.length === 0 ? (
          <Empty title="Satış verisi yok" desc="Seçilen tarih aralığında satış bulunamadı." />
        ) : (
          <div className="border rounded-xl overflow-x-auto" data-testid="bp-table">
            <Table>
              <TableHeader><TableRow className="bg-muted/50">
                <TableHead className="w-10">#</TableHead><TableHead>Marka</TableHead>
                <TableHead className="text-right">Adet</TableHead><TableHead className="text-right">Hasılat</TableHead>
                <TableHead className="text-right">Maliyet</TableHead><TableHead className="text-right">Kâr</TableHead><TableHead className="text-right">Marj</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {brandProf.map((r, i) => (
                  <TableRow key={r.brand} data-testid={`bp-row-${i}`}>
                    <TableCell><span className="h-6 w-6 rounded-md text-[11px] font-black flex items-center justify-center font-head bg-primary/10 text-primary">{i + 1}</span></TableCell>
                    <TableCell><span className="font-semibold text-sm" data-testid={`bp-brand-${i}`}>{r.brand}</span></TableCell>
                    <TableCell className={cn("text-right tabular-nums font-mono", bpMetric === "qty" && "font-bold text-primary")}>{NUM(r.qty)}</TableCell>
                    <TableCell className={cn("text-right tabular-nums font-mono", bpMetric === "revenue" && "font-bold text-primary")}>{TRY(r.revenue)}</TableCell>
                    <TableCell className="text-right tabular-nums font-mono text-muted-foreground">{TRY(r.cost)}</TableCell>
                    <TableCell className={cn("text-right tabular-nums font-mono", bpMetric === "profit" ? (r.profit >= 0 ? "font-bold text-emerald-600" : "font-bold text-destructive") : (r.profit >= 0 ? "text-emerald-600" : "text-destructive"))}>{TRY(r.profit)}</TableCell>
                    <TableCell className={cn("text-right tabular-nums font-mono text-xs", r.margin >= 0 ? "text-emerald-600" : "text-destructive")}>%{NUM(r.margin)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Panel>

      <Panel
        title={tab === "stok" ? "Mevcut Stok Raporu" : tab === "gelen" ? "Gelen Mal Raporu (Stok Girişleri)" : "Giden Mal Raporu (Stok Çıkışları)"}
        subtitle={tab === "stok" ? `${stockRows.length} ürün · stok değerine göre sıralı` : `${moveRows.length} hareket`}
        icon={FileText} className="print-area" testid="report-table-panel"
        actions={<>
          <div className="relative hidden sm:block no-print">
            <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ara..." className="pl-8 h-8 w-44" data-testid="report-search" />
          </div>
          <Button size="sm" variant="outline" className="h-8 no-print" onClick={tab === "stok" ? stockExport : moveExport} data-testid="report-export-excel"><FileDown className="h-4 w-4 mr-1.5 text-emerald-600" />Excel</Button>
        </>}
      >
        <div className="mb-4 no-print">
          <Segmented value={tab} onChange={setTab} testid="tab" options={[["stok", "Stok Raporu", products.length], ["gelen", "Gelen Mal", incoming.length], ["giden", "Giden Mal", outgoing.length]]} />
        </div>
        <div className="border rounded-xl overflow-x-auto">
          {tab === "stok" ? (
            <Table>
              <TableHeader><TableRow className="bg-muted/50">
                <TableHead>Ürün</TableHead><TableHead>Marka</TableHead><TableHead>Kategori</TableHead>
                <TableHead className="text-right">Stok</TableHead><TableHead className="text-right">Birim Maliyet</TableHead><TableHead className="text-right">Satış Fiyatı</TableHead><TableHead className="text-right">Stok Değeri</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {stockRows.slice(0, ROW_CAP).map((p) => (
                  <TableRow key={p.id} data-testid={`report-stock-row-${p.id}`}>
                    <TableCell className="font-semibold">{p.name}{p.code ? <span className="block text-[11px] font-mono text-muted-foreground font-normal">{p.code}</span> : null}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{p.brand || "-"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{p.category || "-"}</TableCell>
                    <TableCell className="text-right tabular-nums font-mono">{NUM(p.quantity)} <span className="text-[10px] text-muted-foreground">{p.unit}</span></TableCell>
                    <TableCell className="text-right tabular-nums font-mono">{TRY(costOf(p))}</TableCell>
                    <TableCell className="text-right tabular-nums font-mono text-muted-foreground">{TRY(p.price)}</TableCell>
                    <TableCell className="text-right tabular-nums font-mono font-bold">{TRY(costOf(p) * p.quantity)}</TableCell>
                  </TableRow>
                ))}
                {stockRows.length > ROW_CAP && <TableRow><TableCell colSpan={7} className="text-center text-xs text-muted-foreground py-3" data-testid="report-row-cap">İlk {ROW_CAP} ürün gösteriliyor · {stockRows.length - ROW_CAP} ürün daha — tamamı için Excel'e aktarın veya arama yapın.</TableCell></TableRow>}
                <TableRow className="bg-muted/50 font-bold"><TableCell colSpan={6} className="text-right font-head uppercase tracking-wider text-xs">Toplam Stok Değeri (Maliyet)</TableCell><TableCell className="text-right tabular-nums font-mono text-primary" data-testid="report-stock-total">{TRY(stockRows.reduce((sum, p) => sum + costOf(p) * p.quantity, 0))}</TableCell></TableRow>
              </TableBody>
            </Table>
          ) : (
            <MoveTable rows={moveRows} />
          )}
        </div>
      </Panel>

      <Dialog open={repDlg} onOpenChange={(o) => !repGen && setRepDlg(o)}>
        <DialogContent className="max-w-sm" data-testid="monthly-report-dialog">
          <DialogHeader><DialogTitle className="font-head text-2xl">Aylık Rapor</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground -mt-2">Seçtiğiniz ayın satış, tahsilat, alış, ödeme ve KDV özetini şık bir PDF olarak indirin.</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Ay</Label>
              <Select value={String(repMonth)} onValueChange={(v) => setRepMonth(Number(v))}>
                <SelectTrigger data-testid="report-month"><SelectValue /></SelectTrigger>
                <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i)}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Yıl</Label>
              <Select value={String(repYear)} onValueChange={(v) => setRepYear(Number(v))}>
                <SelectTrigger data-testid="report-year"><SelectValue /></SelectTrigger>
                <SelectContent>{[0, 1, 2, 3].map((k) => { const y = now.getFullYear() - k; return <SelectItem key={y} value={String(y)}>{y}</SelectItem>; })}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRepDlg(false)} disabled={repGen}>İptal</Button>
            <Button onClick={fetchMonthly} disabled={repGen} data-testid="report-generate">
              {repGen ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}PDF Oluştur
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {repData && (
        <div style={{ position: "fixed", left: "-9999px", top: 0, width: "794px", background: "#fff" }}>
          <div ref={printRef} style={{ padding: "40px", fontFamily: "'Segoe UI', Arial, Helvetica, sans-serif", color: "#1a1a1a", background: "#fff" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "26px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                <div style={{ width: "6px", height: "46px", background: "#ea580c", borderRadius: "3px" }} />
                <div>
                  <div style={{ fontSize: "22px", fontWeight: 800, letterSpacing: "-0.4px" }}>GÜMÜŞALAN ELEKTRİK</div>
                  <div style={{ fontSize: "11px", color: "#999" }}>Elektrik Malzemeleri</div>
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: "15px", fontWeight: 700, color: "#ea580c", letterSpacing: "0.04em" }}>AYLIK RAPOR</div>
                <div style={{ fontSize: "13px", fontWeight: 700, marginTop: "2px" }}>{MONTHS[repData.month - 1]} {repData.year}</div>
                <div style={{ fontSize: "10px", color: "#999", marginTop: "2px" }}>Oluşturma: {fmtDate(todayISO())}</div>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "18px" }}>
              <RepBox label="Toplam Satış" value={TRY(repData.total_sell)} sub={`${repData.invoice_count_sell} fatura`} color="#ea580c" />
              <RepBox label="Toplam Tahsilat" value={TRY(repData.collected)} color="#16A34A" />
              <RepBox label="Toplam Alış" value={TRY(repData.total_buy)} sub={`${repData.invoice_count_buy} fatura`} color="#2563eb" />
              <RepBox label="Toplam Ödeme" value={TRY(repData.paid_out)} color="#64748b" />
            </div>
            <div style={{ background: "#0b0f19", color: "#fff", borderRadius: "12px", padding: "16px 20px", marginBottom: "22px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.1em", opacity: 0.7 }}>KDV Durumu</div>
                <div style={{ fontSize: "11px", opacity: 0.85, marginTop: "4px" }}>Hesaplanan {TRY(repData.kdv_sell)} · İndirilecek {TRY(repData.kdv_buy)}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.1em", opacity: 0.7 }}>Ödenecek KDV</div>
                <div style={{ fontSize: "22px", fontWeight: 800 }}>{TRY(repData.kdv_balance)}</div>
              </div>
            </div>
            <div style={{ fontSize: "13px", fontWeight: 700, marginBottom: "8px" }}>En Çok İşlem Yapılan Cariler</div>
            <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "22px" }}>
              <thead><tr style={{ background: "#f5f5f5" }}><th style={rTh}>Cari</th><th style={rTh}>Tür</th><th style={rThR}>Satış</th><th style={rThR}>Alış</th></tr></thead>
              <tbody>
                {repData.top_contacts.length === 0 ? (
                  <tr><td style={rTd} colSpan={4}>Bu ay işlem yok.</td></tr>
                ) : repData.top_contacts.map((c, i) => (
                  <tr key={i} style={{ background: i % 2 ? "#fafafa" : "#fff" }}>
                    <td style={rTd}>{c.title}</td>
                    <td style={rTd}>{({ musteri: "Müşteri", tedarikci: "Toptancı", her_ikisi: "Her İkisi" })[c.kind] || "—"}</td>
                    <td style={rTdR}>{c.sell ? TRY(c.sell) : "-"}</td>
                    <td style={rTdR}>{c.buy ? TRY(c.buy) : "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ fontSize: "13px", fontWeight: 700, marginBottom: "8px" }}>Bu Ay Vadesi Gelen Çekler</div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr style={{ background: "#f5f5f5" }}><th style={rTh}>Vade</th><th style={rTh}>Cari</th><th style={rTh}>Yön</th><th style={rThR}>Tutar</th><th style={rTh}>Durum</th></tr></thead>
              <tbody>
                {repData.due_cheques.length === 0 ? (
                  <tr><td style={rTd} colSpan={5}>Bu ay vadesi gelen çek yok.</td></tr>
                ) : repData.due_cheques.map((c, i) => (
                  <tr key={i} style={{ background: i % 2 ? "#fafafa" : "#fff" }}>
                    <td style={rTd}>{c.due_date ? fmtDate(c.due_date) : "-"}</td>
                    <td style={rTd}>{c.contact_title}</td>
                    <td style={rTd}>{c.direction === "gelen" ? "Alınan" : "Verilen"}</td>
                    <td style={rTdR}>{TRY(c.amount)}</td>
                    <td style={rTd}>{c.cleared ? "Tahsil edildi" : "Bekliyor"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: "28px", fontSize: "10px", color: "#bbb", textAlign: "center" }}>Bu rapor GÜMÜŞALAN ELEKTRİK yönetim sistemi ile oluşturulmuştur.</div>
          </div>
        </div>
      )}
    </div>
  );
}

const rTh = { padding: "8px 10px", textAlign: "left", fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.05em", color: "#666", fontWeight: 700, borderBottom: "1px solid #eee" };
const rThR = { ...rTh, textAlign: "right" };
const rTd = { padding: "8px 10px", fontSize: "11px", borderBottom: "1px solid #f0f0f0", verticalAlign: "top" };
const rTdR = { ...rTd, textAlign: "right", whiteSpace: "nowrap", fontWeight: 600 };

function RepBox({ label, value, sub, color = "#111" }) {
  return (
    <div style={{ border: "1px solid #eee", borderRadius: "10px", padding: "14px 16px" }}>
      <div style={{ fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#999", fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: "20px", fontWeight: 800, color, marginTop: "4px" }}>{value}</div>
      {sub ? <div style={{ fontSize: "10px", color: "#aaa", marginTop: "2px" }}>{sub}</div> : null}
    </div>
  );
}

// Sıralı cari listesi — oransal çubuk + sıra rozeti
function TopContactCard({ title, subtitle, testid, rows, field, color, icon: Icon }) {
  const max = rows[0]?.[field] || 1;
  const total = rows.reduce((s, c) => s + (c[field] || 0), 0);
  return (
    <Panel title={title} subtitle={subtitle} icon={Icon} testid={testid} actions={rows.length ? <span className="text-xs font-mono tabular-nums text-muted-foreground">Toplam <b className="text-foreground">{TRY(total)}</b></span> : null}>
      {rows.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">Kayıt yok</p> : (
        <div className="space-y-2.5">
          {rows.map((c, i) => (
            <div key={c.id} className="group" data-testid={`${testid}-${i}`}>
              <div className="flex items-center gap-3">
                <span className="h-6 w-6 shrink-0 rounded-md text-[11px] font-black flex items-center justify-center font-head" style={{ background: `${color}1a`, color }}>{i + 1}</span>
                <span className="min-w-0 flex-1 text-sm font-semibold truncate" title={c.title}>{c.title}</span>
                <span className="text-sm font-bold tabular-nums font-mono" style={{ color }}>{TRY(c[field])}</span>
              </div>
              <div className="ml-9 mt-1 h-1.5 rounded-full bg-muted overflow-hidden">
                <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.max(3, Math.round(((c[field] || 0) / max) * 100))}%`, background: color }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

function MoveTable({ rows }) {
  if (!rows.length) return <div className="p-8 text-center text-muted-foreground">Kayıt yok</div>;
  return (
    <Table>
      <TableHeader><TableRow className="bg-muted/50">
        <TableHead>Tarih</TableHead><TableHead>Ürün</TableHead><TableHead className="text-right">Miktar</TableHead><TableHead>Personel</TableHead><TableHead>Not</TableHead>
      </TableRow></TableHeader>
      <TableBody>
        {rows.slice(0, ROW_CAP).map((m) => (
          <TableRow key={m.id}>
            <TableCell className="text-sm tabular-nums font-mono">{fmtDate(m.created_at)}</TableCell>
            <TableCell className="font-semibold">{m.product_name}</TableCell>
            <TableCell className="text-right tabular-nums font-mono">{NUM(m.quantity)}</TableCell>
            <TableCell className="text-sm text-muted-foreground">{m.user_name}</TableCell>
            <TableCell className="text-sm text-muted-foreground">{m.note || "-"}</TableCell>
          </TableRow>
        ))}
        {rows.length > ROW_CAP && <TableRow><TableCell colSpan={5} className="text-center text-xs text-muted-foreground py-3">İlk {ROW_CAP} hareket gösteriliyor · {rows.length - ROW_CAP} kayıt daha — tamamı için Excel'e aktarın.</TableCell></TableRow>}
      </TableBody>
    </Table>
  );
}
